import { atomFactory } from '@utils/recoil/atomFactory';
import { selectorFactory } from '@utils/recoil/selectorFactory';
import { selectorFamilyFactory } from '@utils/recoil/selectorFamilyFactory';
import {
    RECOIL_KEYS,
    RESOURCE_TYPES,
    RECOIL_PERSIST_KEYS,
    AGENDA,
    WINDOW_MD_PX,
} from '@utils/constants';
import { sortResourcesByOrder } from '@utils/network/resources';
import type { Resource } from '@kiute-tech/network-kiute-pro';
import { agendaSettingsAtom } from '@states/agenda';

export type AgendaResource = {
    id: number;
    name: string;
    color: string;
    resourceData: {
        prUnitId?: number;
    } & Resource;
};

export type AgendaResources = Record<string, AgendaResource>;

export const {
    atom: resourcesAtom,
    hook: useResourcesAtom,
} = atomFactory<AgendaResources>({
    key: RECOIL_KEYS.RESOURCES,
    default: {},
    persistKey: RECOIL_PERSIST_KEYS.RESOURCES,
});

export const {
    atom: mkpResourcesAtom,
    hook: useMkpResourcesAtom,
} = atomFactory<number[]>({
    key: RECOIL_KEYS.MKP_RESOURCES,
    default: [],
    persistKey: RECOIL_PERSIST_KEYS.MKP_RESOURCES,
});

export const {
    atom: selectedResourcesIdsState,
    hook: useSelectedResourcesIds,
} = atomFactory<number[]>({
    key: RECOIL_KEYS.SELECTED_RESOURCES_IDS,
    default: [],
    persistKey: RECOIL_PERSIST_KEYS.SELECTED_RESOURCES_IDS,
});

export const {
    family: resourceByAgendaId,
    hook: useResourceByAgendaId,
} = selectorFamilyFactory({
    key: RECOIL_KEYS.RESOURCE_BY_AGENDA_ID,
    get: ({ get }, id) => {
        const discounts = get(resourcesAtom);
        return discounts[Number(id)];
    },
});

export const {
    selector: allResourcesAsArraySelector,
    hook: useResourcesAsArray,
} = selectorFactory({
    key: RECOIL_KEYS.RESOURCES_AS_ARRAY,
    get: ({ get }) => {
        const resources = get(resourcesAtom);
        return Object.values(resources);
    },
});

export const {
    family: resourcesById,
    hook: useResourcesById,
} = selectorFamilyFactory({
    key: RECOIL_KEYS.RESOURCES_BY_ID,
    get: ({ get }, id) => {
        const resources = get(allResourcesAsArraySelector);
        return (
            resources.filter((resource) => resource.resourceData.id === id) ||
            []
        );
    },
});

export const {
    selector: humanResources,
    hook: useHumanResources,
} = selectorFactory({
    key: RECOIL_KEYS.HUMAN_RESOURCES,
    get: ({ get }) =>
        sortResourcesByOrder(
            get(allResourcesAsArraySelector).filter(
                (r) => r.resourceData.objectType === RESOURCE_TYPES.HUMAN,
            ),
        ),
});

export const {
    selector: physicalResources,
    hook: usePhysicalResources,
} = selectorFactory({
    key: RECOIL_KEYS.PHYSICAL_RESOURCES,
    get: ({ get }) =>
        sortResourcesByOrder(
            get(allResourcesAsArraySelector).filter(
                (r) => r.resourceData.objectType === RESOURCE_TYPES.PHYSICAL,
            ),
        ),
});

export const {
    selector: selectedRessources,
    hook: useSelectedResources,
} = selectorFactory({
    key: RECOIL_KEYS.SELECTED_RESOURCES,
    get: ({ get }) => {
        try {
            const resources = get(resourcesAtom);
            const selectedResourcesIds = get(selectedResourcesIdsState);
            const selected = selectedResourcesIds.map((id) => resources?.[id]);
            return selected.filter(Boolean);
        } catch (e) {
            console.warn('useSelectedResources error ', e);
            return [];
        }
    },
});

export const {
    selector: selectedResourcesByAgendaView,
    hook: useSelectedResourcesByAgendaView,
} = selectorFactory({
    key: RECOIL_KEYS.SELECTED_RESOURCES_BY_AGENDA_VIEW,
    get: ({ get }) => {
        const selectedResources = get(selectedRessources);
        const agendaSettings = get(agendaSettingsAtom);
        // This part is not reactive when switching from desktop to mobile. This use case should not happens in production as either desktop or mobile is shown.
        const currentView =
            window?.innerWidth >= WINDOW_MD_PX
                ? agendaSettings.desktopView
                : agendaSettings.mobileView;
        const limit = AGENDA.RESOURCE_LIMITS[currentView];

        if (selectedResources.length <= limit) return selectedResources;
        return selectedResources.slice(0, limit);
    },
});

export const {
    atom: resourcesLoading,
    hook: useResourcesLoading,
} = atomFactory({ key: RECOIL_KEYS.RESOURCES_LOADING, default: true });

export type resourcesGroupByServiceIdType = Record<string, number[]>;

export const {
    selector: resourcesIdGroupByServiceIdSelector,
    hook: useResourceIdsGroupByServiceId,
} = selectorFactory({
    key: RECOIL_KEYS.RESOURCES_GROUPBY_SERVICE_ID,
    get: ({ get }) => {
        const resources = get(resourcesAtom);
        return Object.entries(resources).reduce(
            (result, [resourceId, resource]) => {
                const servicesMap = resource.resourceData.servicesId.reduce(
                    (acc, serviceId: number) =>
                        result[serviceId] !== undefined
                            ? {
                                ...acc,
                                [serviceId]: [
                                    ...result[serviceId],
                                    Number(resourceId),
                                ],
                            }
                            : { ...acc, [serviceId]: [Number(resourceId)] },
                    {} as resourcesGroupByServiceIdType,
                );

                return { ...result, ...servicesMap };
            },
            {} as resourcesGroupByServiceIdType,
        );
    },
});

export const {
    atom: listViewSelectedResourcesAtom,
    hook: useListViewSelectedResourcesAtom,
} = atomFactory<Record<string, boolean>>({
    key: RECOIL_KEYS.LIST_VIEW_SELECTED_RESOURCES,
    default: {},
    persistKey: RECOIL_PERSIST_KEYS.LIST_VIEW_SELECTED_RESOURCES,
});
